import { formatMountingAvlLabel } from '@/components/SystemDesign/AvlFormUtils';
import { useGetAvlItemsQuery } from '@/services/approvedVendorList';
import type { AvlItem } from '@/services/approvedVendorList';
import isNumber from 'lodash/isNumber';
import { useMemo } from 'react';

import {
  AccountDocument,
  MountingHardwareSelectedOption,
  MountingType,
  ProgramType,
  SystemDesign,
  VendorEquipmentTypes,
} from 'types';

interface AvlMountingIndex {
  byId: Map<string, AvlItem>;
  byIdentity: Map<string, AvlItem[]>;
}

// Mirrors the server's mounting identity (manufacturer + sku + category).
const mountingIdentityKey = (manufacturer?: string, sku?: string, category?: string) =>
  `${manufacturer}|${sku}|${category}`;

const buildAvlMountingIndex = (items: AvlItem[]): AvlMountingIndex => {
  const byId = new Map<string, AvlItem>();
  const byIdentity = new Map<string, AvlItem[]>();
  items.forEach((item) => {
    byId.set(item.id, item);
    const key = mountingIdentityKey(item.manufacturer, item.sku, item.category);
    byIdentity.set(key, [...(byIdentity.get(key) ?? []), item]);
  });
  return { byId, byIdentity };
};

interface AvlMountingMatch {
  item?: AvlItem;
  isInAvl: boolean;
}

// Legacy selections may lack avlItemId; an ambiguous identity match is in the AVL but unresolved.
const matchAvlMountingItem = (index: AvlMountingIndex, model: MountingHardwareSelectedOption): AvlMountingMatch => {
  const byId = model.avlItemId ? index.byId.get(model.avlItemId) : undefined;
  if (byId) return { item: byId, isInAvl: true };
  const matches = index.byIdentity.get(mountingIdentityKey(model.manufacturer, model.sku, model.category)) ?? [];
  return { item: matches.length === 1 ? matches[0] : undefined, isInAvl: matches.length > 0 };
};

export const useSetPvSystemInitialValues = (
  account: AccountDocument<ProgramType.solar | ProgramType.doePr | ProgramType.consumerSolar>,
): SystemDesign => {
  const { systemDesign } = account;
  const accountId = account?.id?.toString();
  // Server derives effectiveDate from accountId; never sent by the client.
  const { data: approvedVendorList } = useGetAvlItemsQuery(
    accountId
      ? { accountId, equipmentType: VendorEquipmentTypes.mounting }
      : {
          programType: account.programType || ProgramType.solar,
          orgAlias: account.organizationId,
          equipmentType: VendorEquipmentTypes.mounting,
        },
  );
  const avlMountingHardwareOptions = useMemo(() => approvedVendorList?.data ?? [], [approvedVendorList]);
  const avlIndex = useMemo(() => buildAvlMountingIndex(avlMountingHardwareOptions), [avlMountingHardwareOptions]);
  const savedMountingHardware = systemDesign?.mountingHardware;

  const mountingHardwareWithAddedVals = useMemo(
    () =>
      savedMountingHardware?.map((hardware) => {
        return {
          ...hardware,
          mountingHardwareModels: hardware.mountingHardwareModels?.map((model) => {
            const { item, isInAvl } = matchAvlMountingItem(avlIndex, model);
            const baseLabel = formatMountingAvlLabel(item ?? model);
            return {
              ...model,
              value: `${model.manufacturer}${model.sku}`,
              label: isInAvl ? baseLabel : `${baseLabel}*`,
            };
          }),
        };
      }),
    [savedMountingHardware, avlIndex],
  );

  const initialValues = {
    systemSizeKw: systemDesign?.systemSizeKw ?? '',
    asBuiltSystemSizeKw: systemDesign?.asBuiltSystemSizeKw ?? '',
    systemFirstYearProductionKwh: systemDesign?.systemFirstYearProductionKwh ?? '',
    totalPanelCount: systemDesign?.totalPanelCount || 0,
    panelManufacturer: systemDesign?.panelManufacturer ?? '',
    panelModel: systemDesign?.panelModel ?? '',
    inverterModels: systemDesign?.inverters?.length
      ? systemDesign.inverters.map((inverter) => ({
          model: inverter.model,
          count: inverter.count,
          vintage: inverter.vintage,
          avlItemId: inverter.avlItemId,
        }))
      : [{}],
    inverterManufacturer: systemDesign?.inverters?.[0]?.manufacturer ?? '',
    mountingType: systemDesign?.mountingType ?? MountingType.roof,
    mountingManufacturer: systemDesign?.mountingManufacturer ?? '',
    mountingHardware: mountingHardwareWithAddedVals ?? [],
    newMountingHardwareType: '',
    arrays: systemDesign?.arrays
      ? systemDesign.arrays.map((array) => ({
          ...array,
          monthlySolarAccess: array.monthlySolarAccess?.map((item) => item.accessPercent),
          inverter: isNumber(array.inverter) ? array.inverter + 1 : array.inverter,
          azimuthUnit: array.azimuthUnit ?? 'degrees',
          tiltUnit: array.tiltUnit ?? 'degrees',
        }))
      : [],
    firstYearMonthlyProductionKwh:
      systemDesign?.firstYearMonthlyProductionKwh?.map((item: any) => item.productionKwh as number) ?? [],
    storageManufacturer: systemDesign?.storage?.[0]?.manufacturer ?? '',
    storageModels: systemDesign?.storage?.length
      ? systemDesign.storage.map((storage) => ({
          model: storage.model,
          count: storage.count,
          vintage: storage.vintage,
          avlItemId: storage.avlItemId,
        }))
      : [{}],
    electricityUsageKwh: Array.isArray(account?.electricityUsageKwh)
      ? account.electricityUsageKwh?.map((item) => Math.round(item * 1000) / 1000)
      : [],
    totalElectricityUsageKwh: Array.isArray(account?.electricityUsageKwh)
      ? account.electricityUsageKwh.reduce((acc, curr) => acc + curr, 0)
      : account?.electricityUsageKwh && Number.isFinite(account?.electricityUsageKwh)
        ? account?.electricityUsageKwh
        : 0,
    electricityUsageUpdated: false,
    electricityUsageSource: account?.electricityUsageSource || '',
  };
  return initialValues as unknown as SystemDesign;
};
